/**
 * Local-only storage for imported conversation history.
 *
 * When a user re-hydrates a fresh device from a `ghostcomm-chat-*.json`
 * export, we stash the resulting messages under
 * `ghostcomm_imported_history_{conversationId}` and merge them back into
 * ChatView on render. No server round-trip — the server has already
 * auto-destructed these messages per the 24-hour retention rule, so the
 * exported file IS the source of truth for anything older than 24 h.
 *
 * All entries sit under the `ghostcomm_` prefix so the panic button and
 * Local Data Inspector pick them up automatically.
 */

import { setItem as lsSet, removeItem as lsRemove } from './storage';

const KEY_PREFIX = 'ghostcomm_imported_history_';

const makeKey = (conversationId) => `${KEY_PREFIX}${conversationId}`;

/** Read imported history for a conversation. Returns `[]` when nothing stored. */
export function loadImportedHistory(conversationId) {
  if (!conversationId) return [];
  try {
    const raw = window.localStorage.getItem(makeKey(conversationId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.messages) ? parsed.messages : [];
  } catch {
    return [];
  }
}

/** Read the envelope (messages + metadata) for a conversation, or null. */
export function loadImportedEnvelope(conversationId) {
  if (!conversationId) return null;
  try {
    const raw = window.localStorage.getItem(makeKey(conversationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.messages)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist the messages + a lightweight metadata envelope. */
export function saveImportedHistory(conversationId, messages, meta = {}) {
  if (!conversationId || !Array.isArray(messages)) return;
  const envelope = {
    conversationId,
    importedAt: new Date().toISOString(),
    ...meta,
    messages,
  };
  lsSet(makeKey(conversationId), JSON.stringify(envelope));
}

/** Forget imported history for a conversation (metadata included). */
export function clearImportedHistory(conversationId) {
  if (!conversationId) return;
  lsRemove(makeKey(conversationId));
}
